import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMasterEvent } from '../../../context/MasterEventContext';
import { useAuth } from '../../../context/AuthContext';
import { shuffleboardService } from '../../../services/shuffleboard';
import Avatar from '../../shared/Avatar';

const s = {
  wrap: { padding: 16, display: 'flex', flexDirection: 'column', gap: 16 },
  section: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 },
  row: { display: 'flex', gap: 8, alignItems: 'center' },
  input: { flex: 1, padding: '9px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none' },
  btn: { padding: '9px 16px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnSecondary: { padding: '9px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' },
  btnDanger: { padding: '7px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#f87171', fontSize: 12, cursor: 'pointer' },
  playerRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 },
  scoreCell: { fontSize: 28, fontWeight: 800, color: '#22c55e', minWidth: 50, textAlign: 'right' },
  starterBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(245,158,11,0.15)', color: '#fbbf24', letterSpacing: 0.3 },
  winnerBadge: { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  roundItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', fontSize: 12, color: 'rgba(255,255,255,0.7)' },
  empty: { padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 },
  error: { padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#f87171', fontSize: 12 },
};

export default function ShuffleboardActivity({ activity }) {
  const { masterEvent, members } = useMasterEvent();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Setup form state
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [targetScore, setTargetScore] = useState(21);

  // Score input state per-player
  const [pointsInput, setPointsInput] = useState({});

  const loadGame = useCallback(async () => {
    try {
      // Always read from DynamoDB so all players see each other's updates
      const g = await shuffleboardService.fetchGame(activity.id);
      setGame(g);
    } catch (err) {
      console.warn('[Shuffleboard] loadGame failed:', err);
    } finally {
      setLoading(false);
    }
  }, [activity.id]);

  useEffect(() => { loadGame(); }, [loadGame]);

  // Poll DynamoDB every 2.5s so all players see live updates
  useEffect(() => {
    if (game?.status !== 'active' && game?.status !== 'setup') return;
    const interval = setInterval(loadGame, 2500);
    return () => clearInterval(interval);
  }, [game?.status, loadGame]);

  const isMember = useMemo(
    () => members?.some((m) => (m.user?.id || m.userId) === user?.id),
    [members, user]
  );

  const handleCreate = () => {
    setError('');
    if (selectedPlayerIds.length < 2) { setError('Select at least 2 players'); return; }
    try {
      const players = selectedPlayerIds.map((id) => {
        const m = members.find((mem) => (mem.user?.id || mem.userId) === id);
        return { userId: id, displayName: m?.user?.displayName || 'Player' };
      });
      const newGame = shuffleboardService.createGame({
        activityId: activity.id,
        masterEventId: masterEvent.id,
        createdBy: user.id,
        players,
        targetScore,
      });
      setGame(newGame);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStart = () => {
    try {
      const updated = shuffleboardService.startGame(activity.id);
      setGame(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmitPoints = (scoringUserId) => {
    setError('');
    const points = parseInt(pointsInput[scoringUserId], 10);
    if (!points || points <= 0) { setError('Enter a positive number of points'); return; }
    try {
      const updated = shuffleboardService.submitRound(activity.id, {
        scoringUserId,
        points,
        enteredBy: user.id,
      });
      setPointsInput({ ...pointsInput, [scoringUserId]: '' });
      setGame(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUndo = () => {
    if (!window.confirm('Undo the last round?')) return;
    try {
      const updated = shuffleboardService.undoLastRound(activity.id);
      setGame(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Reset the game? This deletes all scores and starts over.')) return;
    try {
      shuffleboardService.resetGame(activity.id);
      setGame(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const findUserInfo = (userId) => {
    const m = members?.find((mem) => (mem.user?.id || mem.userId) === userId);
    return m?.user || { displayName: 'Player' };
  };

  if (loading) return <div style={s.empty}>Loading shuffleboard...</div>;

  // ═══ SETUP PHASE — no game exists yet ═══
  if (!game) {
    return (
      <div style={s.wrap}>
        <div style={s.section}>
          <div style={s.sectionTitle}>🎯 New Shuffleboard Game</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 0 }}>
            Any member can track points each round. The player who didn't score starts the next round.
          </p>
          {error && <div style={{ ...s.error, marginBottom: 10 }}>{error}</div>}

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...s.sectionTitle, marginBottom: 6 }}>Players</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {members?.map((mem) => {
                const uid = mem.user?.id || mem.userId;
                const selected = selectedPlayerIds.includes(uid);
                return (
                  <label key={uid} style={{ ...s.playerRow, cursor: 'pointer', opacity: selected ? 1 : 0.6 }}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {
                        setSelectedPlayerIds(selected
                          ? selectedPlayerIds.filter((id) => id !== uid)
                          : [...selectedPlayerIds, uid]);
                      }}
                    />
                    <Avatar user={mem.user} size={20} />
                    <span style={{ flex: 1, fontSize: 13 }}>{mem.user?.displayName || 'Unknown'}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ ...s.sectionTitle, marginBottom: 6 }}>Target Score</div>
            <div style={s.row}>
              {[15, 21, 50].map((v) => (
                <button
                  key={v}
                  onClick={() => setTargetScore(v)}
                  style={{
                    ...s.btnSecondary,
                    ...(targetScore === v ? { borderColor: '#22c55e', color: '#22c55e' } : {}),
                  }}
                >
                  {v}
                </button>
              ))}
              <input
                type="number"
                value={targetScore}
                onChange={(e) => setTargetScore(parseInt(e.target.value, 10) || 21)}
                style={{ ...s.input, maxWidth: 80 }}
              />
            </div>
          </div>

          <button onClick={handleCreate} style={s.btn}>Create Game</button>
        </div>
      </div>
    );
  }

  // ═══ SETUP (game created but not started) ═══
  if (game.status === 'setup') {
    return (
      <div style={s.wrap}>
        <div style={s.section}>
          <div style={s.sectionTitle}>🎯 Shuffleboard — Ready to Start</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
            Target: <strong style={{ color: '#fff' }}>{game.targetScore} points</strong> — {game.players.length} players
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {game.players.map((p, i) => (
              <div key={p.userId} style={s.playerRow}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{i + 1}.</span>
                <Avatar user={findUserInfo(p.userId)} size={20} />
                <span style={{ flex: 1, fontSize: 13 }}>{p.displayName}</span>
                {p.userId === game.currentStarterId && <span style={s.starterBadge}>STARTS</span>}
              </div>
            ))}
          </div>

          <div style={s.row}>
            <button onClick={handleStart} style={s.btn}>Start Game</button>
            <button onClick={handleReset} style={s.btnSecondary}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ FINISHED ═══
  if (game.status === 'finished') {
    const winner = game.players.find((p) => p.userId === game.winnerId);
    const sorted = [...game.players].sort((a, b) => (b.score || 0) - (a.score || 0));
    return (
      <div style={s.wrap}>
        <div style={s.section}>
          <div style={{ ...s.sectionTitle, color: '#22c55e' }}>🏆 Game Over</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
            {winner?.displayName} wins with {winner?.score} points!
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {sorted.map((p, i) => (
              <div key={p.userId} style={s.playerRow}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.4)', minWidth: 22 }}>{i + 1}</span>
                <Avatar user={findUserInfo(p.userId)} size={20} />
                <span style={{ flex: 1, fontSize: 13 }}>{p.displayName}</span>
                <span style={s.scoreCell}>{p.score}</span>
                {p.userId === game.winnerId && <span style={s.winnerBadge}>WINNER</span>}
              </div>
            ))}
          </div>
          <div style={s.row}>
            <button onClick={handleReset} style={s.btn}>New Game</button>
          </div>
        </div>

        <RoundsList game={game} findUserInfo={findUserInfo} />
      </div>
    );
  }

  // ═══ ACTIVE PHASE ═══
  const starterInfo = findUserInfo(game.currentStarterId);
  const sortedPlayers = [...game.players].sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <div style={s.wrap}>
      {/* Scoreboard */}
      <div style={s.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={s.sectionTitle}>🎯 Round {game.currentRound} · Target {game.targetScore}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
            <span style={s.starterBadge}>STARTS</span>
            <Avatar user={starterInfo} size={16} />
            <span>{starterInfo.displayName}</span>
          </div>
        </div>

        {error && <div style={{ ...s.error, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sortedPlayers.map((p) => {
            const info = findUserInfo(p.userId);
            const isStarter = p.userId === game.currentStarterId;
            return (
              <div key={p.userId} style={{ ...s.playerRow, ...(isStarter ? { borderLeft: '3px solid #fbbf24' } : {}) }}>
                <Avatar user={info} size={22} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.displayName}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                    {game.targetScore - (p.score || 0)} to win
                  </div>
                </div>
                <span style={s.scoreCell}>{p.score || 0}</span>
                {isMember && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input
                      type="number"
                      min="1"
                      placeholder="pts"
                      value={pointsInput[p.userId] || ''}
                      onChange={(e) => setPointsInput({ ...pointsInput, [p.userId]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitPoints(p.userId); }}
                      style={{ ...s.input, maxWidth: 60, padding: '6px 8px', fontSize: 12 }}
                    />
                    <button
                      onClick={() => handleSubmitPoints(p.userId)}
                      style={{ ...s.btn, padding: '6px 10px', fontSize: 11 }}
                      title="Add points"
                    >+</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ ...s.row, marginTop: 12, justifyContent: 'space-between' }}>
          <button onClick={handleUndo} disabled={game.rounds.length === 0} style={{ ...s.btnSecondary, opacity: game.rounds.length === 0 ? 0.4 : 1 }}>
            ↶ Undo last round
          </button>
          <button onClick={handleReset} style={s.btnDanger}>Reset game</button>
        </div>
      </div>

      <RoundsList game={game} findUserInfo={findUserInfo} />
    </div>
  );
}

function RoundsList({ game, findUserInfo }) {
  if (!game.rounds || game.rounds.length === 0) {
    return (
      <div style={s.section}>
        <div style={s.sectionTitle}>Round History</div>
        <div style={s.empty}>No rounds scored yet</div>
      </div>
    );
  }

  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>Round History</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[...game.rounds].reverse().map((r, i) => {
          const scorer = findUserInfo(r.scoringUserId);
          return (
            <div key={i} style={s.roundItem}>
              <span style={{ width: 28, color: 'rgba(255,255,255,0.3)', fontWeight: 700 }}>R{r.roundNumber}</span>
              <Avatar user={scorer} size={14} />
              <span style={{ flex: 1 }}>{scorer.displayName}</span>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>+{r.points}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
